import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useSearch from "../../../../../../hooks/use-search";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import IconButton from "../../../../../../components/base/button/icon_button";
import {AddOutlined, OpenInNew} from "@mui/icons-material";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import {useHistory} from "react-router-dom";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {useTranslation} from "react-i18next";
import Api from "../../../../../../../core/services/api/api";
import {InventoryTransferListDataGridColumnNames} from "../../../../../../../core/constants/table/data-grid-column-keys";
import DateUtils from "../../../../../../../core/services/date-utils";
import Button from "../../../../../../components/base/button/button";
import CreateInventoryTransferDialog from "../../../../../../components/dialogs/create-transfer-dialog";
import InventoryTransferListSearchSection from "../../../../../../components/search-sections/inventory-transfer-list";
import EnvService from "../../../../../../../core/services/env";

const exportingChunkSize = 100;

const dataGridSaveConfig = {
    name: "InventoryTransferListViewDataGrid",
    version: 1,
};

const InventoryTransferHistoryListView = () => {
    const history = useHistory();
    const {t} = useTranslation('views', {keyPrefix: 'inventory-transfer.list'});
    const [data, setData] = useState([]);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('inventory-transfer-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportInventoryTransferHistory = operatorAccessLevels.packages.bizInventory.exportInventoryTransferHistory;
    const [warehouses, setWarehouses] = useState([]);

    /**
     * Configures notebook tags and behavioral analytics for the transfer list view.
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.inventoryTransferHistoryList);
        configureBehavioralTool((tags) => tags.inventoryTransferHistoryList);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches warehouses from the server (operator list, or v2 when the env flag is on).
     * @type {(function(): Promise<void>)}
     */
    const getWarehouses = useCallback(async () => {
        const response = EnvService.DEV_useWarehouseV2InTransfers
            ? await Api.readAllWarehouses()
            : await Api.getOperatorWarehouses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setWarehouses((response?.data ?? []).map((warehouse) => ({
                ...warehouse,
                name: warehouse.name ?? warehouse.title,
            })));
        }
    }, [isMounted]);


    /**
     * Fetches warehouses on component mount.
     */
    useEffect(() => {
        void getWarehouses();
    }, [getWarehouses]);


    const columns = useMemo(() => [
        {
            name: InventoryTransferListDataGridColumnNames.navigate,
            alignment: DataGridColumnAlignments.center,
            type: DataGridColumnTypes.element,
            pinned: true,
            sortable: false,
            pinnedToggleable: false,
            resizable: false,
            visibilityToggleable: false,
            width: 0,
        },
        {
            title: t('columns.code'),
            name: InventoryTransferListDataGridColumnNames.code,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
        },
        {
            title: t('columns.status'),
            name: InventoryTransferListDataGridColumnNames.status,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
        },
        {
            title: t('columns.fromWarehouseCode'),
            name: InventoryTransferListDataGridColumnNames.fromWarehouseCode,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            width: 140,
            sortable: false,
        },
        {
            title: t('columns.toWarehouseCode'),
            name: InventoryTransferListDataGridColumnNames.toWarehouseCode,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            width: 140,
            sortable: false,
        },
        {
            title: t('columns.totalPartCount'),
            name: InventoryTransferListDataGridColumnNames.totalPartCount,
            type: DataGridColumnTypes.number,
            alignment: DataGridColumnAlignments.right,
            sortable: false,
        },
        {
            title: t('columns.totalRequestedQty'),
            name: InventoryTransferListDataGridColumnNames.totalRequestedQty,
            type: DataGridColumnTypes.number,
            alignment: DataGridColumnAlignments.right,
            sortable: false,
        },
        {
            title: t('columns.totalScannedQty'),
            name: InventoryTransferListDataGridColumnNames.totalScannedQty,
            type: DataGridColumnTypes.number,
            alignment: DataGridColumnAlignments.right,
            sortable: false,
        },
        {
            title: t('columns.submitDateTime'),
            name: InventoryTransferListDataGridColumnNames.submitDateTime,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            width: 180,
        },
        {
            title: t('columns.modifiedDateTime'),
            name: InventoryTransferListDataGridColumnNames.modifiedDateTime,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            width: 180,
        },
        {
            title: t('columns.requiredDate'),
            name: InventoryTransferListDataGridColumnNames.requiredDate,
            type: DataGridColumnTypes.string,
            alignment: DataGridColumnAlignments.left,
            width: 140,
        },
    ], [t]);

    const [savedDataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize,
    } : DefaultPaginationInfo, [dataGridPageSize]);

    /**
     * Fetches transfer list entries from the server.
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = {
            pagination: {
                currentPage: args.paginationInfo?.currentPage ?? DefaultPaginationInfo.currentPage,
                pageSize: args.paginationInfo?.pageSize ?? DefaultPaginationInfo.pageSize,
            },
            sorts: args.orderBy?.field
                ? [{property: args.orderBy.field, isDesc: !!args.orderBy.descending}]
                : [],
        };
        if (args.filters.statusNames?.length) {
            forApi.statusNames = args.filters.statusNames;
        }
        if (args.filters.submitPeriodStart || args.filters.submitPeriodEnd) {
            forApi.submitPeriod = {
                // start of the day in local time
                startDateTime: args.filters.submitPeriodStart ? DateUtils.localToServer(DateUtils.serverToLocal(args.filters.submitPeriodStart).startOf('day')) : null,
                // end of the day in local time
                endDateTime: args.filters.submitPeriodEnd ? DateUtils.localToServer(DateUtils.serverToLocal(args.filters.submitPeriodEnd).endOf('day')) : null,
            };
        }
        const response = await Api.listInventoryTransfers(forApi, args.cancelTokenSource?.token);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag && !forExport) {
            setData(response?.data?.items ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted]);

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result);
    }, [fetchEntries]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {
            statusNames: [],
            submitPeriodStart: null,
            submitPeriodEnd: null,
        },
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings, true);
    const {performSearch} = searchData;

    /**
     * Navigates to the transfer information view.
     */
    const navigateToTransferInformationView = useCallback((row) => {
        history.push(routeFunctions.inventory.inventoryTransfer.single(row.id));
    }, [history]);

    /**
     * Formats date-time values for the data grid.
     */
    const formatDateTimeCell = useCallback((value) => {
        if (!value) {
            return '--';
        }
        return DateUtils.serverToLocal(value).format(DateUtils.dateTimeFormat);
    }, []);

    /**
     * Formats date values for the data grid.
     */
    const formatDateCell = useCallback((value) => {
        if (!value) {
            return '--';
        }
        return DateUtils.serverToLocal(value).format(DateUtils.dateFormat);
    }, []);

    /**
     * Creates a csv formatted file of all the entries of the purchase generators.
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const getExportInfo = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag)
                return;

            if (!res?.data?.items?.length)
                break;

            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        if (!entries.length)
            return;
        return {
            title: t('title'),
            body: entries?.map(e => {
                const fromWarehouse = warehouses.find(w =>
                    (e.fromWarehouseId != null && w.id === e.fromWarehouseId) || (w.code === e.fromWarehouseCode));
                const toWarehouse = warehouses.find(w =>
                    (e.toWarehouseId != null && w.id === e.toWarehouseId) || (w.code === e.toWarehouseCode));
                return ({
                    [t('columns.code')]: e.code,
                    [t('columns.status')]: e.statusName,
                    [t('columns.fromWarehouseCode')]: fromWarehouse ? `${fromWarehouse.code} - ${fromWarehouse.name}` : e.fromWarehouseCode,
                    [t('columns.toWarehouseCode')]: toWarehouse ? `${toWarehouse.code} - ${toWarehouse.name}` : e.toWarehouseCode,
                    [t('columns.totalPartCount')]: e.totalPartCount,
                    [t('columns.totalRequestedQty')]: e.totalRequestedQty,
                    [t('columns.totalScannedQty')]: e.totalScannedQty,
                    [t('columns.submitDateTime')]: formatDateTimeCell(e.submitDateTime),
                    [t('columns.modifiedDateTime')]: formatDateTimeCell(e.modifiedDateTime),
                    [t('columns.requiredDate')]: formatDateCell(e.requiredDate),
                })
            })
        }
    }, [fetchEntries, formatDateCell, formatDateTimeCell, isMounted, searchData, t, warehouses])

    const dataGridRows = useMemo(() => data.map((item) => {
        const fromWarehouse = warehouses.find(w =>
            (item.fromWarehouseId != null && w.id === item.fromWarehouseId) || (w.code === item.fromWarehouseCode));
        const toWarehouse = warehouses.find(w =>
            (item.toWarehouseId != null && w.id === item.toWarehouseId) || (w.code === item.toWarehouseCode));
        return ({
            key: item.id,
            data: item,
            cells: {
                [InventoryTransferListDataGridColumnNames.navigate]:
                    <IconButton
                        color={'secondary'}
                        onPressed={() => navigateToTransferInformationView(item)}
                    >
                        <OpenInNew className={'icon normal'}/>
                    </IconButton>,
                [InventoryTransferListDataGridColumnNames.code]: item.code,
                [InventoryTransferListDataGridColumnNames.status]: item.statusName,
                [InventoryTransferListDataGridColumnNames.submitDateTime]: formatDateTimeCell(item.submitDateTime),
                [InventoryTransferListDataGridColumnNames.modifiedDateTime]: formatDateTimeCell(item.modifiedDateTime),
                [InventoryTransferListDataGridColumnNames.requiredDate]: formatDateCell(item.requiredDate),
                [InventoryTransferListDataGridColumnNames.fromWarehouseCode]: fromWarehouse ? `${fromWarehouse.code} - ${fromWarehouse.name}` : item.fromWarehouseCode,
                [InventoryTransferListDataGridColumnNames.toWarehouseCode]: toWarehouse ? `${toWarehouse.code} - ${toWarehouse.name}` : item.toWarehouseCode,
                [InventoryTransferListDataGridColumnNames.totalPartCount]: item.totalPartCount,
                [InventoryTransferListDataGridColumnNames.totalRequestedQty]: item.totalRequestedQty,
                [InventoryTransferListDataGridColumnNames.totalScannedQty]: item.totalScannedQty,
            },
        })
    }), [data, formatDateCell, formatDateTimeCell, navigateToTransferInformationView, warehouses]);

    /**
     * Opens the create transfer dialog.
     */
    const openCreateDialog = useCallback(() => {
        setCreateDialogOpen(true);
    }, []);

    /**
     * Closes the create transfer dialog.
     */
    const closeCreateDialog = useCallback(() => {
        setCreateDialogOpen(false);
    }, []);

    /**
     * Refreshes the list after a transfer is created and optionally navigates to detail.
     */
    const onTransferCreated = useCallback((createdTransfer) => {
        performSearch();
        if (createdTransfer?.id) {
            history.push(routeFunctions.inventory.inventoryTransfer.single(createdTransfer.id));
        }
    }, [history, performSearch]);

    return (
        <>
            <div className={'p-3'}>
                <div className={'d-flex justify-content-between align-content-center mb-4'}>
                    <p className={'view-title'}>
                        {t('title')}
                    </p>
                    <Button
                        variant={'contained'}
                        color={'primary'}
                        startIcon={<AddOutlined/>}
                        onClick={openCreateDialog}
                    >
                        {t('createTransfer')}
                    </Button>
                </div>
                <InventoryTransferListSearchSection searchData={searchData}/>
                <div className={'mb-5'}/>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{columns}}
                    columns={savedDataGridColumns}
                    rows={dataGridRows}
                    hideFooterActions
                    getExportInfo={canExportInventoryTransferHistory ? getExportInfo : undefined}
                    state={{
                        sortBy: searchData.orderBy,
                        pagination: searchData.paginationInfo,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                    onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                    onCurrentPageChanged={(_, current) =>
                        searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) =>
                        searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
            {
                createDialogOpen &&
                <CreateInventoryTransferDialog
                    open={createDialogOpen}
                    close={closeCreateDialog}
                    onCreated={onTransferCreated}
                />
            }
        </>
    );
};

export default InventoryTransferHistoryListView;
